import React                  from 'react';
import { createPortal }       from 'react-dom';
import { observer }           from 'mobx-react-lite';
import { TokaMapInstance }    from '@tokamap/core';
import { gridStepInMercator } from '@tokamap/core';
import { lngLatToMercator }   from '@tokamap/core';
import currentEditorStore     from '@/TokamapEditor/stores/currentEditorStore';
import useDesignTokens        from '@/hooks/useDesignTokens';
import { metersToUnit }       from '@/TokamapEditor/helpers/mapKind';
import type { DesignTokens }  from '@/hooks/useDesignTokens';
import type { LengthUnit }    from '@/TokamapEditor/helpers/mapKind';

export const RULER_SIZE_PX = 22;

const MIN_LABEL_SPACING_PX = 70;
const ROTATION_TOLERANCE_DEG = 0.01;
const NICE_STEPS = [1, 2, 5];
const METERS_PER_UNIT: Record<LengthUnit, number> = { mm: 0.001, cm: 0.01, m: 1 };

type Adapter = TokaMapInstance['mapAdapter'];

type Axis = {
	startMeters: number;
	metersPerPx: number;
};

type Frame = {
	horizontal: Axis;
	vertical: Axis;
	cursor: { x: number; y: number; } | null;
};

function planMeters(adapter: Adapter, x: number, y: number): [number, number] {
	const lngLat = adapter.screenToLngLat(x, y);
	const [mercatorX, mercatorY] = lngLatToMercator(lngLat);
	const mercatorPerMeter = gridStepInMercator(1, lngLat[1]);

	return [mercatorX / mercatorPerMeter, -mercatorY / mercatorPerMeter];
}

function readFrame(adapter: Adapter, container: HTMLElement, cursor: Frame['cursor']): Frame | null {
	if (Math.abs(adapter.getRotation()) > ROTATION_TOLERANCE_DEG || adapter.getPitch() > ROTATION_TOLERANCE_DEG) return null;

	const width = container.clientWidth;
	const height = container.clientHeight;
	const [left, top] = planMeters(adapter, RULER_SIZE_PX, RULER_SIZE_PX);
	const [right] = planMeters(adapter, width, RULER_SIZE_PX);
	const [, bottom] = planMeters(adapter, RULER_SIZE_PX, height);

	return {
		horizontal: { startMeters: left, metersPerPx: (right - left) / (width - RULER_SIZE_PX) },
		vertical: { startMeters: top, metersPerPx: (bottom - top) / (height - RULER_SIZE_PX) },
		cursor: cursor && { x: cursor.x - RULER_SIZE_PX, y: cursor.y - RULER_SIZE_PX },
	};
}

const GRID_LABEL_MULTIPLES = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000];
const MIN_MINOR_SPACING_PX = 5;

function ticks(metersPerPx: number, unit: LengthUnit, gridStep: number | null): { label: number; minor: number; } {
	if (gridStep) {
		const multiple = GRID_LABEL_MULTIPLES.find(candidate => (candidate * gridStep) / metersPerPx >= MIN_LABEL_SPACING_PX);

		if (multiple) {
			const label = multiple * gridStep;

			return { label, minor: gridStep / metersPerPx >= MIN_MINOR_SPACING_PX ? gridStep : label / 5 };
		}
	}

	const label = tickStep(metersPerPx, unit);

	return { label, minor: label / 5 };
}

function tickStep(metersPerPx: number, unit: LengthUnit): number {
	const minUnits = (MIN_LABEL_SPACING_PX * metersPerPx) / METERS_PER_UNIT[unit];
	let magnitude = 10 ** Math.floor(Math.log10(minUnits));

	for (;;) {
		for (const nice of NICE_STEPS) {
			if (nice * magnitude >= minUnits) return nice * magnitude * METERS_PER_UNIT[unit];
		}
		magnitude *= 10;
	}
}

function formatTick(meters: number, unit: LengthUnit): string {
	return metersToUnit(meters, unit).toLocaleString('fr-FR').replace('-', '\u2212');
}

function roundedRect(context: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) {
	context.beginPath();
	context.roundRect(x, y, width, height, radius);
	context.fill();
}

function drawRuler(
	canvas: HTMLCanvasElement,
	axis: Axis,
	vertical: boolean,
	unit: LengthUnit,
	gridStep: number | null,
	cursorPx: number | null,
	tokens: DesignTokens,
) {
	const ratio = window.devicePixelRatio || 1;
	const length = vertical ? canvas.clientHeight : canvas.clientWidth;
	const context = canvas.getContext('2d');

	canvas.width = (vertical ? RULER_SIZE_PX : length) * ratio;
	canvas.height = (vertical ? length : RULER_SIZE_PX) * ratio;

	if (!context || !axis.metersPerPx) return;

	context.setTransform(ratio, 0, 0, ratio, 0, 0);

	if (vertical) {
		context.translate(0, length);
		context.rotate(-Math.PI / 2);
	}

	context.fillStyle = tokens.surface.card;
	context.fillRect(0, 0, length, RULER_SIZE_PX);

	const { label: step, minor } = ticks(Math.abs(axis.metersPerPx), unit, gridStep);
	const endMeters = axis.startMeters + axis.metersPerPx * length;
	const first = Math.ceil(Math.min(axis.startMeters, endMeters) / minor) * minor;
	const last = Math.max(axis.startMeters, endMeters);
	const along = (px: number) => (vertical ? length - px : px);
	const toPx = (meters: number) => along((meters - axis.startMeters) / axis.metersPerPx);

	context.font = `500 10px ${getComputedStyle(canvas).fontFamily}`;
	context.textAlign = 'center';
	context.textBaseline = 'middle';
	context.lineWidth = 1;
	context.strokeStyle = tokens.border.default;
	context.fillStyle = tokens.text.muted;
	context.beginPath();

	for (let meters = first; meters <= last; meters += minor) {
		const position = Math.round(toPx(meters)) + 0.5;
		const major = Math.abs(meters / step - Math.round(meters / step)) < 1e-6;
		const tick = major ? 6 : 3;

		context.moveTo(position, RULER_SIZE_PX - tick);
		context.lineTo(position, RULER_SIZE_PX);

		if (major) context.fillText(formatTick(Math.round(meters / minor) * minor, unit), position, RULER_SIZE_PX / 2 - 2);
	}

	context.moveTo(0, RULER_SIZE_PX - 0.5);
	context.lineTo(length, RULER_SIZE_PX - 0.5);
	context.stroke();

	if (cursorPx === null || cursorPx < 0 || cursorPx > length) return;

	const label = `${formatTick(axis.startMeters + cursorPx * axis.metersPerPx, unit)} ${unit}`;
	const labelWidth = context.measureText(label).width + 12;
	const cursorAlong = along(cursorPx);
	const labelLeft = Math.min(Math.max(cursorAlong - labelWidth / 2, 2), length - labelWidth - 2);

	context.fillStyle = tokens.color.primary;
	roundedRect(context, labelLeft, 3, labelWidth, RULER_SIZE_PX - 6, 4);
	context.fillRect(Math.round(cursorAlong) - 0.5, RULER_SIZE_PX - 3, 1.5, 3);

	context.fillStyle = tokens.surface.card;
	context.fillText(label, labelLeft + labelWidth / 2, RULER_SIZE_PX / 2);
}

const PlanRulers = observer(() => {
	const container = currentEditorStore.get?.mapAdapter.getContainerElement();

	if (!container || !currentEditorStore.showsRulers) return null;

	return createPortal(<Rulers container={container} />, container);
});

export default PlanRulers;

const Rulers = observer(({ container }: { container: HTMLElement; }) => {
	const tokens = useDesignTokens();
	const adapter = currentEditorStore.get.mapAdapter;
	const unit = currentEditorStore.lengthUnit;
	const gridStep = currentEditorStore.displayedGridStep;
	const topRef = React.useRef<HTMLCanvasElement>(null);
	const leftRef = React.useRef<HTMLCanvasElement>(null);
	const [visible, setVisible] = React.useState(true);
	const tokensRef = React.useRef(tokens);

	tokensRef.current = tokens;

	React.useEffect(() => {
		let cursor: Frame['cursor'] = null;
		let frameRequest = 0;

		const draw = () => {
			frameRequest = 0;
			const frame = readFrame(adapter, container, cursor);

			setVisible(!!frame);
			if (!frame || !topRef.current || !leftRef.current) return;

			drawRuler(topRef.current, frame.horizontal, false, unit, gridStep, frame.cursor?.x ?? null, tokensRef.current);
			drawRuler(leftRef.current, frame.vertical, true, unit, gridStep, frame.cursor?.y ?? null, tokensRef.current);
		};

		const schedule = () => {
			if (!frameRequest) frameRequest = requestAnimationFrame(draw);
		};

		const onMove = (event: MouseEvent) => {
			const bounds = container.getBoundingClientRect();
			cursor = { x: event.clientX - bounds.left, y: event.clientY - bounds.top };
			schedule();
		};

		const onLeave = () => {
			cursor = null;
			schedule();
		};

		draw();

		const unsubscribe = adapter.onCameraMove(schedule);
		const resize = new ResizeObserver(schedule);

		resize.observe(container);
		container.addEventListener('mousemove', onMove);
		container.addEventListener('mouseleave', onLeave);

		return () => {
			cancelAnimationFrame(frameRequest);
			unsubscribe();
			resize.disconnect();
			container.removeEventListener('mousemove', onMove);
			container.removeEventListener('mouseleave', onLeave);
		};
	}, [adapter, container, unit, gridStep, tokens.surface.card]);

	return (
		<div className="plan-rulers" hidden={!visible}>
			<canvas ref={topRef} className="plan-ruler plan-ruler--top" />
			<canvas ref={leftRef} className="plan-ruler plan-ruler--left" />

			<div
				className="plan-ruler-corner"
				style={{ background: tokens.surface.card, color: tokens.text.muted, borderColor: tokens.border.default }}
			>
				{unit}
			</div>
		</div>
	);
});
